import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { getProjectPhaseLabel } from "../../../../../constants/project-phase";
import { NewDealJourneyParams } from "../../../../../models";
import { IStateOrigin } from "../../../../../navigators/navigateStateObjects";
import { SearchProjectsRequest } from "../../../../../service/publicquery";
import { searchProjects } from "../../../../../service/publicquery/PublicQueryService.full";
import { FilterGroups, Status } from "../../../../../service/Shared";
import { useAuth } from "../../../../../useAuth";
import { getNewDealRoute, getProjectDetailsByUuid } from "../../../../../utils/routes";
import { dataGridMapFilterCriteria } from "../../../../../widget";
import { InvestmentProjectCardProps } from "./ProjectCard";

interface UseProjectCardProps {
  projectUuid: string;
  landholdingUuid?: string;
}

interface UseProjectCardReturnData {
  landholdingProjects: InvestmentProjectCardProps[] | undefined;
  showLandholdingProjects: boolean;
  onProjectCardClick: () => void;
  onLandholdingCardClick: () => void;
  onNewDealClick: (objectUuid: string, objectType: string, objectName?: string) => void;
}

// eslint-disable-next-line unused-imports/no-unused-vars
export const useProjectCard = ({ projectUuid, landholdingUuid }: UseProjectCardProps): UseProjectCardReturnData => {
  const { currentUserType } = useAuth();
  const location = useLocation();
  const [showLandholdingProjects, setShowLandholdingProjects] = useState<boolean>(false);
  const [landholdingProjects, setLandholdingProjects] = useState<InvestmentProjectCardProps[]>();
  const navigate = useNavigate();
  const onProjectCardClick = (): void => {
    navigate(getProjectDetailsByUuid(projectUuid, currentUserType), {
      state: { origin: { goBackText: "Back to investment directory", from: location.pathname } as IStateOrigin },
    });
  };

  const onLandholdingCardClick = async (): Promise<void> => {
    if (landholdingProjects == null) {
      const filterCriteria = dataGridMapFilterCriteria([]);
      filterCriteria.landholdings = {
        some: {
          groupUuid: {
            operator: "eq",
            value: landholdingUuid,
          },
        },
      };
      const request: SearchProjectsRequest & FilterGroups<SearchProjectsRequest["filter"]> = {
        paging: {
          limit: 50,
          beforeCursor: null,
          afterCursor: null,
        },
        distinctLandholding: false,
        // landholdingUuid,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        filter: { results: filterCriteria },
      };
      const res = await searchProjects(request, "investmentDirectory");

      if (res.status === Status.Success) {
        setLandholdingProjects(
          res.data?.results.map((d) => ({
            isLandholdingProject: true,
            uuid: d.uuid,
            name: d.displayName,
            imageSource:
              d?.listing?.content?.images && d?.listing?.content?.images[0]
                ? d.listingFiles?.find((el) => el.uuid === d?.listing?.content?.images[0])?.file.url
                : "",
            addressCountryCode: d.addressCountryCode || "",
            addressRegion: d.addressRegion,
            developerSmallLogoFileUrl: d.developer?.files ? d.developer.files[0]?.file.url : "",
            developerDisplayName: d.developer?.displayName || undefined,
            standardDisplayName: d.standard?.displayName || undefined,
            cachedAvailableQuantity: d.cachedUnitAvailableForSale || 0,
            quantityType: d.cachedQuantityType,
            phase: getProjectPhaseLabel(d.cachedPhase),
            status: d.status,
            projectType: d.projectType?.displayName || "",
            projectTypeUuid: d.projectType?.uuid || "",
            areaNetHa: d.areaNetHa,
            avgPrice: d.calculatedIssuancePriceAvg,
            tco2e: d.tco2e,
            landholding: null,
          }))
        );
      }
    }
    setShowLandholdingProjects(!showLandholdingProjects);
  };

  const onNewDealClick = (objectUuid: string, objectType: string, objectName?: string): void => {
    const params = new URLSearchParams();

    params.set(NewDealJourneyParams.objectUuid, objectUuid);
    params.set(NewDealJourneyParams.objectType, objectType);

    if (objectName) {
      params.set(NewDealJourneyParams.objectName, objectName);
    }

    navigate(getNewDealRoute(params.toString()));
  };

  return {
    landholdingProjects,
    showLandholdingProjects,
    onProjectCardClick,
    onLandholdingCardClick,
    onNewDealClick,
  };
};
